import React, { useMemo, useState } from 'react';
import { PanelListCard } from '@tranquility-ai/theme-components/panel-list-card';
import { formatTime } from '@/shared/functions';
import { hasTimelineDate, formatSlideDateUtc } from '@/shared/clientFunctions';
import { stripMarkdownForSearch } from '@/shared/citationScrollNeedle';
import type { Slide } from '@/shared/types';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import {
  SidePanel,
  SIDE_PANEL_LIST_CLASS,
  SIDE_PANEL_LIST_SCROLL_CLASS,
  SIDE_PANEL_LIST_SCROLL_SYMMETRIC_CLASS,
  SIDE_PANEL_SYMMETRIC_INSET_CLASS,
} from './side-panel';

export const TIMELINE_EVENTS_PANEL_WIDTH_PX = 330;

const toDescriptionPreview = (description: string): string =>
  stripMarkdownForSearch(description.replace(/<[^>]*>/g, '')).trim();

export const getTimelineEventDisplayDate = (slide: Slide): string => {
  if (slide.id === 'TITLE_SLIDE') return 'Case Introduction';
  if (!hasTimelineDate(slide) && slide.slideSubTitle && slide.slideSubTitle.trim() !== '') {
    return slide.slideSubTitle;
  }
  if (hasTimelineDate(slide)) {
    const formatted = formatSlideDateUtc(slide.date!, 'DD MMM YYYY');
    if (formatted) return formatted;
  }
  if (slide.displayDate && slide.displayDate.trim() !== '') return slide.displayDate;
  return 'Date not specified';
};

export const getTimelineEventTitle = (slide: Slide): string =>
  slide.slideTitle?.trim() || slide.slideSubTitle?.trim() || 'Untitled event';

export const getTimelineEventMeta = (slide: Slide): string => {
  const dateLabel = getTimelineEventDisplayDate(slide);
  if (slide.id === 'TITLE_SLIDE') return dateLabel;
  if (slide.isTimeValid && slide.time) return `${dateLabel} · ${formatTime(slide.time)}`;
  return dateLabel;
};

export const getTimelineEventSearchLabel = (slide: Slide): string => {
  const title = getTimelineEventTitle(slide);
  const meta = getTimelineEventMeta(slide);
  const descriptionPreview = slide.slideDescription?.trim()
    ? toDescriptionPreview(slide.slideDescription)
    : '';
  return [title, meta, descriptionPreview].filter(Boolean).join(' ');
};

interface TimelineEventsPanelProps {
  events: Slide[];
  selectedId?: string;
  onSelectEvent: (slide: Slide, event: React.MouseEvent<HTMLButtonElement>) => void;
  onClose: () => void;
}

export const TimelineEventsPanel: React.FC<TimelineEventsPanelProps> = ({
  events,
  selectedId,
  onSelectEvent,
  onClose,
}) => {
  const t = useTimePilotT();
  const [filterQuery, setFilterQuery] = useState('');
  const normalizedQuery = filterQuery.trim().toLowerCase();

  const filteredEvents = useMemo(() => {
    if (!normalizedQuery) return events;
    return events.filter((slide) =>
      getTimelineEventSearchLabel(slide).toLowerCase().includes(normalizedQuery),
    );
  }, [events, normalizedQuery]);

  const hasEvents = events.length > 0;

  return (
    <SidePanel
      side="left"
      title={t('timelineEvents.title')}
      className={`min-h-0 flex-1 ${SIDE_PANEL_SYMMETRIC_INSET_CLASS}`}
      onCollapse={onClose}
      collapseLabel={t('timelineEvents.close')}
      sectionLabel={hasEvents ? t('timelineEvents.section') : undefined}
      aria-label={t('timelineEvents.aria')}
    >
      {hasEvents && (
        <label className="mb-3 block w-full shrink-0 px-[var(--tp-side-panel-body-pl)]">
          <span className="sr-only">{t('timelineEvents.filterAria')}</span>
          <input
            type="search"
            value={filterQuery}
            onChange={(event) => setFilterQuery(event.target.value)}
            placeholder={t('timelineEvents.filter')}
            className="w-full rounded-[4px] border border-transparent bg-white px-3 py-2 text-sm text-black placeholder:text-black/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-brand)]"
          />
        </label>
      )}
      <div className={`custom-scrollbar2 min-h-0 flex-1 ${SIDE_PANEL_LIST_SCROLL_CLASS} ${SIDE_PANEL_LIST_SCROLL_SYMMETRIC_CLASS}`}>
        {hasEvents ? (
          filteredEvents.length > 0 ? (
            <ol className={SIDE_PANEL_LIST_CLASS}>
              {filteredEvents.map((slide, index) => {
                const isCurrent = slide.id === selectedId;
                const title = getTimelineEventTitle(slide);
                const meta = getTimelineEventMeta(slide);

                return (
                  <li key={slide.id ?? `timeline-event-${index}`}>
                    <PanelListCard
                      data-testid="HorizontalTimelineMenuItem"
                      isSelected={isCurrent}
                      ariaCurrent={isCurrent}
                      title={title}
                      meta={meta}
                      onClick={(event) => onSelectEvent(slide, event)}
                    />
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="flex min-h-[120px] w-full items-center justify-center">
              <p className="m-0 text-sm font-light text-[var(--text-inverse,#ffffff)]/70">
                {t('timelineEvents.noMatch')}
              </p>
            </div>
          )
        ) : (
          <div className="flex min-h-[120px] w-full items-center justify-center">
            <p className="m-0 text-sm font-light text-[var(--text-inverse,#ffffff)]/70">
              {t('timelineEvents.none')}
            </p>
          </div>
        )}
      </div>
    </SidePanel>
  );
};
